import { useRolodexEdit } from 'features/fde'
import { FfsSharedValuesContext } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import {
  FfsLtaReport,
  FfsLtaReportBase,
} from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsReports'
import {
  createFfsStrategy,
  PartSpecificReportDataArgs,
  SerializePartSpecificDataFn,
  useFfsStrategy,
} from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/FfsStrategyContext'
import { FfsMawpSectionProps } from 'features/indicationTriage/cards/ffsCalc/mawpSection/FfsMawpSection'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { useUpdateIndicationContext } from 'features/indicationTriage/updateIndicationContext'
import { useRolodexEntries, useRolodexHistogram } from 'features/rolodex'
import { useCallback, useContext, useMemo, useState } from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementUnit,
  RolodexEntryJson,
  RolodexEntryPutJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'

import {
  FfsLtaReportDataSchema,
  validateFfsLtaRolodexEntry,
} from './ffsPublishApiSchema'
import { FfsLevel, FfsPart, FfsResultType } from './ffsTypes'
import { ExcludeSetters } from './typeUtils'

const FFS_REPORT_TYPE = 'ffs-lta-report' as RolodexEntryType

export const RESULT_OUTCOME_COLORS: Record<FfsResultType, string> = {
  [FfsResultType.Pass]: 'green',
  [FfsResultType.Derate]: 'orange',
  [FfsResultType.Fail]: 'red',
  [FfsResultType.Pending]: 'gray',
  [FfsResultType.Skipped]: 'blue',
}

export const RESULT_OUTCOME_LABELS: Record<FfsResultType, string> = {
  [FfsResultType.Pass]: 'Pass',
  [FfsResultType.Derate]: 'Derate',
  [FfsResultType.Fail]: 'Fail',
  [FfsResultType.Pending]: 'Pending',
  [FfsResultType.Skipped]: 'Skipped',
}

export const RESULT_OUTCOME_DESCRIPTIONS: Record<FfsResultType, string> = {
  [FfsResultType.Pass]:
    'Equipment can be ran for the amount of time specified by the user',
  [FfsResultType.Derate]:
    "Equipment needs to be derated to run for the amount of time specified by user and is within the user's acceptable derate limit",
  [FfsResultType.Fail]:
    "Equipment cannot be ran for the amount of time specified by the user due to exceed the user's acceptable derate limit",
  [FfsResultType.Pending]: 'FFS Screening has not been completed yet',
  [FfsResultType.Skipped]: 'FFS Screening was intentionally skipped',
}

export function geckoMeasurementToJson(measurement: GeckoMeasurement): {
  num: number
  unit: string
} {
  return { num: measurement.num, unit: measurement.unit.id }
}

export function nullableGeckoMeasurementToJson(
  measurement: GeckoMeasurement | null
): { num: number; unit: string } | null {
  return measurement ? geckoMeasurementToJson(measurement) : null
}

// Type-safe helper for objects containing only GeckoMeasurement | null properties
export function transformMeasurementObject<
  T extends Record<string, GeckoMeasurement | null>,
>(obj: T): Record<keyof T, { num: number; unit: string } | null> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      nullableGeckoMeasurementToJson(value),
    ])
  ) as Record<keyof T, { num: number; unit: string } | null>
}

export function jsonToGeckoMeasurement(json: {
  num: number
  unit: string
}): GeckoMeasurement {
  const unit = GeckoMeasurementUnit.GetByID(json.unit)
  if (!unit) {
    throw new Error(`Unknown measurement unit: ${json.unit}`)
  }
  return GeckoMeasurement.CreateFromValue(json.num, unit)
}

export function nullableJsonToGeckoMeasurement(
  json: { num: number; unit: string } | null
): GeckoMeasurement | null {
  return json ? jsonToGeckoMeasurement(json) : null
}

export function transformJsonMeasurementObject<
  T extends Record<string, { num: number; unit: string } | null>,
>(obj: T): Record<keyof T, GeckoMeasurement | null> {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      nullableJsonToGeckoMeasurement(value),
    ])
  ) as Record<keyof T, GeckoMeasurement | null>
}

export function useExistingOrganizationScenarios(): {
  scenarios: ReadonlyArray<string>
  isLoading: boolean
} {
  const { organization } = useIndicationData()
  const { data, isLoading } = useRolodexHistogram(
    useMemo(
      () =>
        organization
          ? [
              {
                select: [
                  {
                    type: FFS_REPORT_TYPE,
                    field: 'outcome.scenario',
                    group_by: true,
                    label: 'value',
                  },
                ],
                type: FFS_REPORT_TYPE,
                tags: { organization: organization.id },
                limit: 100,
              },
            ]
          : [],
      [organization]
    )
  )
  const scenarios = useMemo(
    () => data?.flat().map((e) => e['value'] as string) ?? [],
    [data]
  )
  return useMemo(() => ({ scenarios, isLoading }), [scenarios, isLoading])
}

function toRolodexEntryPut(
  ffsReport: FfsLtaReport,
  tags: RolodexEntryTagsType,
  serializePartSpecificDataFn: SerializePartSpecificDataFn
): RolodexEntryPutJson {
  const { corrosionOnInnerSurface, ...dataValuesMeasurements } =
    ffsReport.data_values

  const {
    selectedMawpCalcThickness,
    targetDate,
    inspectionDate,
    ...userParamsMeasurements
  } = ffsReport.user_params

  const { calcType, ...mawpMeasurements } = ffsReport.mawp

  // Serialize base report data (common to all parts)
  const baseData = {
    user_params: {
      ...transformMeasurementObject(userParamsMeasurements),
      selectedMawpCalcThickness,
      targetDate,
      inspectionDate,
    },
    data_values: {
      ...transformMeasurementObject(dataValuesMeasurements),
      corrosionOnInnerSurface,
    },
    mawp: {
      ...transformMeasurementObject(mawpMeasurements),
      calcType,
    },
    outcome: ffsReport.outcome,
    ffs_level: ffsReport.ffs_level,
    ffs_part: ffsReport.ffs_part,
    is_default: ffsReport.is_default ?? false,
  }

  // Use strategy to serialize part-specific data
  const partSpecificData = serializePartSpecificDataFn(ffsReport)

  // Merge base and part-specific data
  const data = {
    ...baseData,
    ...partSpecificData,
  }

  // Validate the data structure with Zod schema before returning
  try {
    FfsLtaReportDataSchema.parse(data)
  } catch (error) {
    console.error('Invalid FFS LTA report data being serialized:', error)
    throw new Error(
      `Failed to serialize FFS LTA report: Invalid data structure`
    )
  }

  return {
    id: ffsReport.id,
    type: FFS_REPORT_TYPE,
    data,
    tags,
  }
}

function fromRolodexEntry(entry: RolodexEntryJson): FfsLtaReport {
  // Validate the entry structure with Zod schema
  const validatedEntry = validateFfsLtaRolodexEntry(entry)
  const data = validatedEntry.data

  const { corrosionOnInnerSurface, ...dataValuesMeasurements } =
    data.data_values
  const {
    selectedMawpCalcThickness,
    targetDate,
    inspectionDate,
    ...userParamsMeasurements
  } = data.user_params
  const { calcType, ...mawpMeasurements } = data.mawp

  // Deserialize base report data (common to all parts)
  const baseReport = {
    id: validatedEntry.id,
    user_params: {
      ...transformJsonMeasurementObject(userParamsMeasurements),
      selectedMawpCalcThickness,
      targetDate,
      inspectionDate,
    } as FfsLtaReportBase['user_params'],
    data_values: {
      ...transformJsonMeasurementObject(dataValuesMeasurements),
      corrosionOnInnerSurface,
    } as FfsLtaReportBase['data_values'],
    mawp: {
      ...transformJsonMeasurementObject(mawpMeasurements),
      calcType,
    } as FfsLtaReportBase['mawp'],
    outcome: data.outcome as FfsLtaReportBase['outcome'],
    ffs_part: data.ffs_part,
    ffs_level: data.ffs_level,
    is_default: data.is_default,
  }

  // Use strategy to deserialize part-specific data
  // Note: This needs to be separate creation since we're extracting part from the data
  const ffsPart = data.ffs_part as FfsPart
  const strategy = createFfsStrategy(ffsPart, undefined)
  const partSpecificData = strategy.report.deserializePartSpecificData(data)

  // Merge base and part-specific data
  return {
    ...baseReport,
    ...partSpecificData,
  } as FfsLtaReport
}

export function useSavedReports(): {
  reports: ReadonlyArray<FfsLtaReport>
  isLoading: boolean
  forceReload: () => Promise<void>
  setDefaultReport: (id: string | undefined) => Promise<void>
  isSaving: boolean
} {
  const { indication } = useIndicationData()
  const { entries, isLoading, forceRefresh } = useRolodexEntries(
    useMemo(
      () =>
        indication
          ? {
              type: FFS_REPORT_TYPE,
              tags: { indication: indication.id },
            }
          : undefined,
      [indication]
    )
  )

  const { rolodexPatch } = useRolodexEdit()
  const [isSaving, setIsSaving] = useState(false)

  const reports = useMemo(() => entries.map(fromRolodexEntry), [entries])

  const { refreshIndications } = useUpdateIndicationContext()
  const setDefaultReport = useCallback(
    async (id: string | undefined) => {
      const patches = reports
        .filter(
          (report) =>
            (report.id === id && !report.is_default) ||
            (report.id !== id && report.is_default)
        )
        .map((report) => ({
          id: report.id,
          data: { is_default: report.id === id },
        }))
      const defaultReport = reports.find((report) => report.id === id)
      const indicationUpdatePatches = indication
        ? [
            {
              id: indication.id,
              data: {
                classificationInfo: {
                  classification: defaultReport?.outcome.result ?? 'UNKNOWN',
                },
              },
            },
          ]
        : []
      if (patches.length || indicationUpdatePatches.length) {
        setIsSaving(true)
        await rolodexPatch([...patches, ...indicationUpdatePatches])
          .then(() => forceRefresh())
          .then(() => refreshIndications())
          .finally(() => setIsSaving(false))
      }
    },
    [reports, rolodexPatch, forceRefresh, indication, refreshIndications]
  )

  return useMemo(
    () => ({
      reports,
      isLoading: isLoading,
      forceReload: forceRefresh,
      setDefaultReport,
      isSaving,
    }),
    [reports, isLoading, forceRefresh, setDefaultReport, isSaving]
  )
}

export function useSaveReport(
  partSpecificArgs: PartSpecificReportDataArgs,
  mawpProps: Omit<
    ExcludeSetters<FfsMawpSectionProps>,
    'loading' | 'ffsLevel' | 'addLongitudinalStress' | 'showMawpCriteria'
  >,
  ffsPart: FfsPart,
  ffsLevel: FfsLevel
): {
  saveOutcome: (
    id: string,
    outcome: FfsLtaReportBase['outcome']
  ) => Promise<void>
  isSaving: boolean
} {
  const { strategy } = useFfsStrategy()
  const {
    fca,
    fcaMl,
    corrosionRate,
    corrosionRateMl,
    targetDate,
    inspectionDate,
    ffsDataValues,
    jointEfficiency,
    yieldStrength,
    selectedMawpCalcThickness,
    calcType,
  } = useContext(FfsSharedValuesContext)

  const { indication, unit, site, organization, component } =
    useIndicationData()
  const [saving, setSaving] = useState(false)
  const tags = useMemo(() => {
    return {
      indication: indication?.id,
      component: component?.id,
      unit: unit?.id,
      site: site?.id,
      organization: organization?.id,
    }
  }, [component?.id, indication?.id, organization?.id, site?.id, unit?.id])
  const { rolodexPut } = useRolodexEdit()
  const saveOutcome = useCallback(
    async (id: string, outcome: FfsLtaReportBase['outcome']) => {
      const baseReport = {
        id,
        user_params: {
          fca,
          fcaMl,
          corrosionRate,
          corrosionRateMl,
          targetDate,
          inspectionDate,
          selectedMawpCalcThickness,
        },
        data_values: {
          tmm: ffsDataValues?.tmm ?? null,
          trd: ffsDataValues?.trd ?? null,
          tc: ffsDataValues?.tc ?? null,
          longitudinalFlawLength: ffsDataValues?.longitudinalFlawLength ?? null,
          latitudinalFlawLength: ffsDataValues?.latitudinalFlawLength ?? null,
          innerDiameter: ffsDataValues?.innerDiameter ?? null,
          outerDiameter: ffsDataValues?.outerDiameter ?? null,
          lambda: ffsDataValues?.lambda ?? null,
          nominal: ffsDataValues?.nominal ?? null,
          t_ml: ffsDataValues?.t_ml ?? null,
          d_ml: ffsDataValues?.d_ml ?? null,
          corrosionOnInnerSurface:
            ffsDataValues?.corrosionOnInnerSurface ?? false,
        },
        mawp: {
          e: mawpProps.qualityFactor,
          y: mawpProps.pressureCoefficient,
          w: jointEfficiency,
          s_y: yieldStrength,
          tsl: mawpProps.tsl,
          mawp_c: mawpProps.mawp_c,
          mawp_l: mawpProps.mawp_l,
          mawp_design: mawpProps.mawpDesign,
          mawpr: mawpProps.mawpr,
          calcType: calcType,
        },
        outcome,
        ffs_part: ffsPart,
        ffs_level: ffsLevel,
      }

      const partSpecificData =
        strategy.report.getPartSpecificReportData(partSpecificArgs)

      // Combine base report with part-specific data
      const newReport: FfsLtaReport = { ...baseReport, ...partSpecificData }

      const newReportJson = toRolodexEntryPut(
        newReport,
        tags,
        strategy.report.serializePartSpecificData
      )
      setSaving(true)
      await rolodexPut([newReportJson])
        .catch((e) => console.error(e))
        .finally(() => {
          setSaving(false)
        })
    },
    [
      fca,
      fcaMl,
      corrosionRate,
      corrosionRateMl,
      targetDate,
      inspectionDate,
      selectedMawpCalcThickness,
      calcType,
      ffsDataValues,
      mawpProps,
      jointEfficiency,
      yieldStrength,
      ffsPart,
      ffsLevel,
      strategy,
      partSpecificArgs,
      tags,
      rolodexPut,
    ]
  )

  return useMemo(
    () => ({ saveOutcome, isSaving: saving }),
    [saveOutcome, saving]
  )
}
